/**
 * Standardized error handling utility for BOARD
 * 
 * This module provides error handling utilities that include tenant context
 * and standardized error formatting for consistent error handling across the application.
 */

import { AxiosError } from 'axios';

// Error codes that match the backend ErrorCode enum
export enum ErrorCode {
  // General errors (1000-1999)
  UNKNOWN_ERROR = 1000,
  CONFIGURATION_ERROR = 1001,
  VALIDATION_ERROR = 1002,
  PROCESSING_ERROR = 1003,
  
  // Authentication/Authorization errors (2000-2999)
  AUTHENTICATION_ERROR = 2000,
  AUTHORIZATION_ERROR = 2001,
  TOKEN_EXPIRED = 2002,
  INVALID_CREDENTIALS = 2003,
  
  // Tenant errors (3000-3999)
  TENANT_NOT_FOUND = 3000,
  TENANT_INACTIVE = 3001,
  TENANT_SUSPENDED = 3002,
  FEATURE_NOT_ENABLED = 3003,
  RATE_LIMIT_EXCEEDED = 3004,
  
  // Resource errors (4000-4999)
  RESOURCE_NOT_FOUND = 4000,
  MODEL_NOT_FOUND = 4001,
  DATA_NOT_FOUND = 4002,
  
  // Integration errors (5000-5999)
  INTEGRATION_ERROR = 5000,
  API_ERROR = 5001,
  TIMEOUT_ERROR = 5002,
  CONNECTION_ERROR = 5003,
  
  // Frontend specific errors (6000-6999)
  NETWORK_ERROR = 6000,
  RENDERING_ERROR = 6001,
  STATE_ERROR = 6002
}

// Standard error response structure from backend
export interface ErrorResponse {
  error: {
    code: number;
    name: string;
    message: string;
    trace_id: string;
    timestamp: string;
    tenant_id?: string;
    details?: Record<string, any>;
  };
}

// Frontend error structure with tenant context
export interface AppError {
  code: ErrorCode;
  message: string;
  originalError?: any;
  tenant_id?: string;
  traceId?: string;
  timestamp: string;
  details?: Record<string, any>;
  httpStatus?: number;
}

/**
 * Create a standardized application error
 */
export function createAppError(
  message: string,
  code: ErrorCode = ErrorCode.UNKNOWN_ERROR,
  originalError?: any,
  details?: Record<string, any>
): AppError {
  // Get tenant ID from localStorage
  const userData = localStorage.getItem('userData');
  let tenant_id: string | undefined;
  
  if (userData) {
    try {
      const parsedUserData = JSON.parse(userData);
      tenant_id = parsedUserData.tenant_id;
    } catch (e) {
      console.error('Failed to parse user data from localStorage', e);
    }
  }
  
  // If tenant ID is not in localStorage, try to get it from hostname
  if (!tenant_id) {
    const hostname = window.location.hostname;
    const hostParts = hostname.split('.');
    
    // Check if this is a tenant subdomain
    if (hostParts.length > 2 && hostParts[0] !== 'www') {
      tenant_id = hostParts[0];
    }
  }
  
  return {
    code,
    message,
    originalError,
    tenant_id,
    traceId: generateTraceId(),
    timestamp: new Date().toISOString(),
    details: details || {}
  };
}

/**
 * Generate a unique trace ID for error tracking
 */
function generateTraceId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Parse an error from an Axios response
 */
export function parseAxiosError(error: AxiosError): AppError {
  // Default error
  let appError: AppError = createAppError(
    'An unexpected error occurred',
    ErrorCode.UNKNOWN_ERROR,
    error
  );
  
  // Add HTTP status if available
  if (error.response) {
    appError.httpStatus = error.response.status;
  }
  
  // Check if this is a network error
  if (error.message === 'Network Error') {
    appError.code = ErrorCode.NETWORK_ERROR;
    appError.message = 'Network error: Unable to connect to the server';
    return appError;
  }
  
  // Check if this is a timeout
  if (error.code === 'ECONNABORTED') {
    appError.code = ErrorCode.TIMEOUT_ERROR;
    appError.message = 'Request timed out';
    return appError;
  }
  
  // If we have a response, try to parse the error
  if (error.response && error.response.data) {
    const responseData = error.response.data as Record<string, any>;
    
    // Check if this is a standard error response
    if (responseData && typeof responseData === 'object' && 'error' in responseData) {
      const errorData = responseData.error as Record<string, any>;
      
      appError = {
        code: (errorData.code as ErrorCode) || appError.code,
        message: (errorData.message as string) || appError.message,
        originalError: error,
        tenant_id: (errorData.tenant_id as string) || appError.tenant_id,
        traceId: (errorData.trace_id as string) || appError.traceId,
        timestamp: (errorData.timestamp as string) || appError.timestamp,
        details: (errorData.details as Record<string, any>) || appError.details,
        httpStatus: error.response.status
      };
    } 
    // Check if this is a validation error from Pydantic
    else if (responseData && typeof responseData === 'object' && 'detail' in responseData && Array.isArray(responseData.detail)) {
      appError.code = ErrorCode.VALIDATION_ERROR;
      appError.message = 'Validation error';
      appError.details = {
        validationErrors: responseData.detail.map((item: any) => ({
          field: Array.isArray(item.loc) ? item.loc.join('.') : String(item.loc),
          message: item.msg as string
        }))
      };
    }
    // Handle string error messages
    else if (typeof responseData === 'string') {
      appError.message = responseData;
    }
    // Handle other error formats
    else if (responseData && typeof responseData === 'object' && 'message' in responseData) {
      appError.message = responseData.message as string;
    }
  }
  
  // Map HTTP status codes to error codes if we don't have a specific error code
  if (appError.code === ErrorCode.UNKNOWN_ERROR && appError.httpStatus) {
    switch (appError.httpStatus) {
      case 400:
        appError.code = ErrorCode.VALIDATION_ERROR;
        break;
      case 401:
        appError.code = ErrorCode.AUTHENTICATION_ERROR;
        break;
      case 403:
        appError.code = ErrorCode.AUTHORIZATION_ERROR;
        break;
      case 404:
        appError.code = ErrorCode.RESOURCE_NOT_FOUND;
        break;
      case 429:
        appError.code = ErrorCode.RATE_LIMIT_EXCEEDED;
        break;
      case 500:
        appError.code = ErrorCode.PROCESSING_ERROR;
        break;
      case 502:
      case 503:
      case 504:
        appError.code = ErrorCode.API_ERROR;
        break;
    }
  }
  
  return appError;
}

/**
 * Log an error with tenant context
 */
export function logError(error: AppError): void {
  const tenantPrefix = error.tenant_id ? `[Tenant: ${error.tenant_id}]` : '[No Tenant]';
  const errorCode = ErrorCode[error.code] || 'UNKNOWN';
  
  console.error(
    `${tenantPrefix} ${errorCode}(${error.code}): ${error.message} - Trace ID: ${error.traceId}`,
    {
      error,
      timestamp: new Date().toISOString()
    }
  );
  
  // Here you could add integration with error tracking services like Sentry
  // if (window.Sentry) {
  //   window.Sentry.captureException(error.originalError || error, {
  //     extra: {
  //       code: error.code,
  //       tenant_id: error.tenant_id,
  //       traceId: error.traceId,
  //       details: error.details
  //     }
  //   });
  // }
}

/**
 * Handle an error with standardized logging and optional callback
 */
export function handleError(
  error: any,
  callback?: (appError: AppError) => void
): AppError {
  let appError: AppError;
  
  // Convert to AppError if needed
  if (error.code !== undefined && error.message && error.timestamp) {
    // Already an AppError
    appError = error;
  } else if (error.isAxiosError) {
    // Axios error
    appError = parseAxiosError(error);
  } else {
    // Generic error
    appError = createAppError(
      error.message || 'An unexpected error occurred',
      ErrorCode.UNKNOWN_ERROR,
      error
    );
  }
  
  // Log the error
  logError(appError);
  
  // Call the callback if provided
  if (callback) {
    callback(appError);
  }
  
  return appError;
}

/**
 * Check if an error is a specific type
 */
export function isErrorType(error: AppError, code: ErrorCode): boolean {
  return error.code === code;
}

/**
 * Get a user-friendly error message
 */
export function getUserFriendlyErrorMessage(error: AppError): string {
  // Default message
  let message = 'An unexpected error occurred. Please try again later.';
  
  // Customize based on error code
  switch (error.code) {
    case ErrorCode.AUTHENTICATION_ERROR:
    case ErrorCode.TOKEN_EXPIRED:
      message = 'Your session has expired. Please log in again.';
      break;
    case ErrorCode.AUTHORIZATION_ERROR:
      message = 'You do not have permission to perform this action.';
      break;
    case ErrorCode.NETWORK_ERROR:
      message = 'Unable to connect to the server. Please check your internet connection.';
      break;
    case ErrorCode.TIMEOUT_ERROR:
      message = 'The request timed out. Please try again later.';
      break;
    case ErrorCode.VALIDATION_ERROR:
      if (error.details?.validationErrors?.length > 0) {
        const firstError = error.details.validationErrors[0];
        message = `Validation error: ${firstError.message} (${firstError.field})`;
      } else {
        message = 'The submitted data is invalid. Please check your input and try again.';
      }
      break;
    case ErrorCode.RESOURCE_NOT_FOUND:
      message = 'The requested resource was not found.';
      break;
    case ErrorCode.RATE_LIMIT_EXCEEDED:
      message = 'Rate limit exceeded. Please try again later.';
      break;
    case ErrorCode.FEATURE_NOT_ENABLED:
      message = 'This feature is not enabled for your tenant.';
      break;
    case ErrorCode.TENANT_NOT_FOUND:
    case ErrorCode.TENANT_INACTIVE:
    case ErrorCode.TENANT_SUSPENDED:
      message = 'Your tenant account is not active. Please contact your administrator.';
      break;
    default:
      // Use the error message if it's not a generic one
      if (error.message && 
          error.message !== 'An unexpected error occurred' && 
          error.message !== 'Network Error') {
        message = error.message;
      }
  }
  
  return message;
}
